/**
 * WO completion — shown only when every service is done. A green banner
 * with the tech's own total time + the "Complete work" CTA (hands the WO
 * back to the advisor), then a receipt per SERVICE: its name, who did it
 * and for how long, and its parts. Notes live on the job page (WoJobNotes),
 * not on this receipt (2026-09-22).
 */
import { Check } from '@phosphor-icons/react'
import { ButtonSpinner } from '@vollgas/shared/ui'
import { formatDuration } from '@vollgas/shared/utils'
import { ApiError } from '@/apis/apiClient'
import type { WoDetailService, WorkOrderDetail } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { serviceView, serviceViewMinutes } from './serviceView'
import { hasPendingProposal, myMinutes } from './woDetail'

interface WoCompletionProps {
  readonly wo: WorkOrderDetail
  readonly now: number
  readonly onComplete: () => void
  readonly completing?: boolean
  /** Backend rejected the finish — 409, empty WO or services still open. */
  readonly error?: Error | null
}

export function WoCompletion({ wo, now, onComplete, completing = false, error = null }: WoCompletionProps) {
  const { t } = useI18n()
  const mine = myMinutes(wo, now)
  // Say what actually went wrong. Every one of these used to fall through
  // to "log some time or add a part first", which was true for exactly one
  // of them — so a technician locked out by assignment was told to fix
  // their bookkeeping, and doing that changed nothing.
  //
  // The CTA only renders once everything is done, so services-open here
  // means the job changed underneath us: someone approved a proposal or
  // added work while this screen was open.
  const errorMessage = (() => {
    if (!(error instanceof ApiError)) return t.workOrder.completeError
    if (error.code.endsWith('work-order-tasks-open')) return t.workOrder.completeBlockedByTasks
    if (error.code.endsWith('work-order-forbidden')) return t.workOrder.completeErrForbidden
    if (error.code.endsWith('work-order-empty-on-complete')) return t.workOrder.completeErrEmpty
    // The family code: the server's sentence is specific (it names the
    // invoice standing in the way), which the generic line cannot be.
    const base = error.code.split('.')[0] ?? ''
    if (base.endsWith('work-order-transition-invalid')) {
      const m = error.message?.trim()
      if (m && m !== error.code && /\s/.test(m)) return m
    }
    return t.workOrder.completeError
  })()

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border-2 border-green bg-green-bg px-4 py-3">
        <span className="flex items-center gap-2 text-14 font-bold text-glass-text">
          <Check size={16} weight="bold" className="text-green" aria-hidden="true" />
          {t.workOrder.allTasksDone(formatDuration(mine, { days: true }))}
        </span>
        <button
          type="button"
          onClick={onComplete}
          disabled={completing}
          className="inline-flex items-center gap-1.5 rounded-full bg-green px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
        >
          {completing ? <ButtonSpinner /> : <>{t.workOrder.completeWork} <Check size={14} weight="bold" aria-hidden="true" /></>}
        </button>
      </div>

      {error && (
        <p role="alert" className="rounded-md bg-accent/10 px-3 py-2 text-13 font-semibold text-accent">
          {errorMessage}
        </p>
      )}

      {hasPendingProposal(wo) && (
        <p className="rounded-md border border-glass-border bg-glass-fill px-3 py-2 text-12-5 text-glass-text-muted">
          {t.workOrder.proposalPending}
        </p>
      )}

      <div className="flex flex-col gap-2">
        {wo.services
          .filter((svc) => svc.state === 'done')
          .map((svc) => <ServiceReceipt key={svc.id} service={svc} now={now} />)}
      </div>

      <p className="text-center text-11-5 text-glass-text-muted">{t.workOrder.completeHint}</p>
    </div>
  )
}

/** One finished service: name, who did it and for how long, and its parts. */
function ServiceReceipt({ service, now }: { readonly service: WoDetailService; readonly now: number }) {
  const { t } = useI18n()
  const view = serviceView(service)
  const who = view.mine ? t.tasks.you : view.mechanicName
  return (
    <div className="rounded-md border border-glass-border bg-glass-fill px-4 py-2.5 text-glass-text-muted">
      <div className="flex items-center justify-between gap-3 text-12-5">
        <span className="min-w-0 truncate">
          ✓ <span className="font-semibold text-glass-text">{service.name}</span>{'  '}
          {who} · {formatDuration(serviceViewMinutes(view, now), { days: true })}
        </span>
        {service.parts.length > 0 && (
          <span className="flex-shrink-0">{t.workOrder.partsCount(service.parts.length)}</span>
        )}
      </div>
    </div>
  )
}
